import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BetheaProgress } from "../components/site/BetheaProgress";
import { DonorWall } from "../components/site/DonorWall";
import { Heart, ArrowRight, Crown, Copy } from "lucide-react";
import { api, BRAND } from "../lib/api";
import { toast } from "sonner";

const FUNDS = {
  church: { label: "The Surrender Church", purposes: ["Tithe", "Offering", "Building Fund", "Missions & Outreach", "Pastor's Blessing", "Where Needed Most"] },
  bethea: { label: "House of Bethea · Veteran Housing", purposes: ["Veteran Housing Fund", "Rent Assistance", "Furnishings & Supplies", "Where Needed Most"] },
};

export default function Give() {
  const [params] = useSearchParams();
  const [fund, setFund] = useState(params.get("fund") === "bethea" ? "bethea" : "church");
  const PURPOSES = FUNDS[fund].purposes;
  const [amount, setAmount] = useState("");
  const [purpose, setPurpose] = useState(PURPOSES[0]);
  const pickFund = (k) => { setFund(k); setPurpose(FUNDS[k].purposes[0]); };
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [onWall, setOnWall] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt < 1) return toast.error("Please enter an amount of $1 or more");
    if (amt > 10000) return toast.error("For gifts over $10,000, please contact the church directly");
    setBusy(true);
    try {
      const r = await api.post("/stripe/create-session", {
        origin_url: window.location.origin,
        kind: "tithe",
        amount: amt,
        purpose, name, email, notes, fund, show_on_wall: fund === "bethea" && onWall,
      });
      if (r.data?.url) { window.location.href = r.data.url; return; }
      toast.error("Could not start secure checkout.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not process your gift right now.");
    } finally { setBusy(false); }
  };

  const copyHandle = (t) => { navigator.clipboard.writeText(t); toast.success("Copied!"); };

  return (
    <div data-testid="page-give">
      <section className="container-narrow pt-16 pb-10">
        <div className="label-luxe">Give · Sow</div>
        <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] mt-4 max-w-4xl">
          Sow with <span className="italic gold-text">joy</span>.<br/>
          Reap <span className="fuchsia-text italic">abundance</span>.
        </h1>
        <p className="mt-7 text-white/90 text-lg leading-relaxed max-w-2xl">
          Thank you for partnering with The Surrender Church. Every tithe, offering, and seed you sow fuels
          ministry, missions, and the lives being transformed through surrender.
        </p>
      </section>

      <section className="container-narrow pb-24">
        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3">
            <form onSubmit={submit} className="card-luxe" data-testid="give-form">
              <div className="text-xs uppercase tracking-[0.3em] font-semibold text-[#D4AF37]">Secure gift by card</div>
              <h2 className="font-serif text-3xl mt-3 text-white">Enter your amount</h2>

              <div className="mt-6">
                <label className="label-luxe">Give to</label>
                <div className="grid sm:grid-cols-2 gap-2 mt-2">
                  {Object.entries(FUNDS).map(([k, f]) => (
                    <button key={k} type="button" data-testid={`fund-${k}`} onClick={() => pickFund(k)}
                      className={`px-4 py-3 rounded-xl border text-sm font-semibold transition-colors text-left ${
                        fund === k ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#ECC23D]" : "border-white/10 text-white/90 hover:border-white/30"}`}>
                      {f.label}
                    </button>
                  ))}
                </div>
                {fund === "bethea" && <p className="text-xs text-white/80 mt-2" data-testid="bethea-note">Your gift goes directly to safe, supportive housing for veterans in Brunswick, Georgia.</p>}
                {fund === "bethea" && <BetheaProgress />}
                {fund === "bethea" && <DonorWall />}
              </div>

              <div className="mt-6">
                <label className="label-luxe">Amount (USD)</label>
                <div className="relative mt-2">
                  <span className="absolute left-5 top-1/2 -translate-y-1/2 font-serif text-3xl text-[#ECC23D]">$</span>
                  <input
                    data-testid="give-amount"
                    type="number"
                    inputMode="decimal"
                    min="1"
                    step="0.01"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e)=>setAmount(e.target.value)}
                    required
                    className="input-luxe !pl-12 !text-3xl !font-serif !py-5"
                  />
                </div>
                <p className="text-xs text-white/70 mt-2">Any amount $1 or above. 100% goes to {FUNDS[fund].label}.</p>
              </div>

              <div className="mt-5">
                <label className="label-luxe">Purpose</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                  {PURPOSES.map((p) => (
                    <button
                      key={p}
                      type="button"
                      data-testid={`purpose-${p.toLowerCase().replace(/[^a-z]+/g,'-')}`}
                      onClick={() => setPurpose(p)}
                      className={`px-3 py-2.5 rounded-xl border text-sm font-semibold transition text-left ${
                        fund === "bethea"
                          ? `bg-[#F02078] text-white hover:bg-[#d81b6a] ${purpose === p ? "border-[#ECC23D] ring-2 ring-[#ECC23D]" : "border-[#F02078]"}`
                          : purpose === p ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#ECC23D]" : "border-white/10 text-white/90 hover:border-white/30"
                      }`}
                    >{fund === "bethea" && purpose === p ? `✓ ${p}` : p}</button>
                  ))}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 mt-5">
                <input data-testid="give-name" placeholder="Name (optional)" value={name} onChange={(e)=>setName(e.target.value)} className="input-luxe"/>
                <input data-testid="give-email" type="email" placeholder="Email (optional — for receipt)" value={email} onChange={(e)=>setEmail(e.target.value)} className="input-luxe"/>
              </div>

              <textarea data-testid="give-notes" placeholder="Prayer request or dedication (optional)" rows={3} value={notes} onChange={(e)=>setNotes(e.target.value)} className="input-luxe mt-3"/>
              {fund === "bethea" && (
                <label className="mt-3 flex items-start gap-3 text-sm text-white cursor-pointer" data-testid="donor-wall-optin">
                  <input type="checkbox" checked={onWall} onChange={(e) => setOnWall(e.target.checked)} data-testid="donor-wall-checkbox" className="mt-1 w-4 h-4 accent-[#D4AF37]" />
                  <span>Show my name on the House of Bethea Donor Wall <span className="text-white/80">(first name & last initial only — amounts are never shown{onWall && !name.trim() ? "; please enter your name above" : ""})</span></span>
                </label>
              )}

              <button data-testid="give-submit" disabled={busy} className="btn-fuchsia w-full mt-6 !py-4 !text-base">
                <Heart className="w-5 h-5"/>
                {busy ? "Redirecting…" : `Give ${amount ? `$${parseFloat(amount||0).toFixed(2)}` : ""} Securely`}
                {!busy && <ArrowRight className="w-4 h-4"/>}
              </button>
              <p className="text-xs text-white/70 text-center mt-3">
                Powered by Stripe · Visa · Mastercard · Amex · Discover · Apple Pay · Google Pay
              </p>
            </form>
          </div>

          <aside className="lg:col-span-2 space-y-6">
            <div className="card-luxe">
              <div className="text-xs uppercase tracking-[0.3em] font-semibold text-[#D4AF37]">Prefer another way?</div>
              <h3 className="font-serif text-2xl mt-2 text-white">Zelle or Cash App</h3>
              <p className="text-white/80 text-sm mt-2">You can also give directly from your banking app.</p>
              <div className="mt-5 space-y-3">
                <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-white/[0.04] border border-white/10">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Zelle</div>
                    <div className="font-serif text-xl text-[#ECC23D] break-all">{BRAND.zelle}</div>
                    <div className="text-xs text-white/70 mt-0.5">Pastor Shalonda Raymond</div>
                  </div>
                  <button onClick={() => copyHandle(BRAND.zelle)} className="btn-ghost-gold !py-2 !px-3 text-xs"><Copy className="w-3.5 h-3.5"/>Copy</button>
                </div>
                <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-white/[0.04] border border-white/10">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Cash App</div>
                    <div className="font-serif text-xl text-[#ECC23D] break-all">{BRAND.cashapp}</div>
                    <div className="text-xs text-white/70 mt-0.5">Pastor Shalonda Raymond</div>
                  </div>
                  <button onClick={() => copyHandle(BRAND.cashapp)} className="btn-ghost-gold !py-2 !px-3 text-xs"><Copy className="w-3.5 h-3.5"/>Copy</button>
                </div>
              </div>
            </div>

            <div className="card-dark p-7 rounded-[28px] border border-[#D4AF37]/30">
              <Crown className="w-7 h-7 text-[#ECC23D]" />
              <p className="mt-4 font-serif text-xl italic text-white leading-snug">
                "Bring ye all the tithes into the storehouse… and prove me now herewith, saith the Lord of hosts, if I will not open you the windows of heaven."
              </p>
              <p className="mt-3 text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Malachi 3:10</p>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
